/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Route, Routes, HashRouter } from 'react-router';
import { PrimeReactProvider } from 'primereact/api';
import { HeaderNavbar } from '@/components/HeaderNavbar.tsx';
import { ProcessesProvider, TypeProcessing } from 'ogc-processes-components';
import { GuardApp } from '@/GuardApp';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRoutes } from '@/constants/routes.ts';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { TriggersPage } from './pages/TriggersPage';

import { get } from '@/utils/networks.ts';

import '@/i18n.ts';
import 'primeflex/primeflex.min.css';
import 'primeicons/primeicons.css';
import '@/App.scss';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { StoreContextProvider } from './contexts/StoreContext';
import { Loader } from './components/Loader';
import { ErrorBoundaryWrapper } from './components/ErrorBoundaryWrapper';
import { AlertMessage } from './components/AlertMessage';
import { ConfigENV } from './init';
import { CopyRightFooter } from './components/CopyRightFooter';
import { SpacesPage } from './pages/SpacesPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { JobsPage } from './pages/JobsPage';
import { DocsPage } from './pages/DocsPage';
import { AntFlowServiceStatusUp } from './types/models';
import { OIDCServiceFactory } from './services/auth/factory';

interface Props {
  children?: React.ReactNode;
  antflowServerConfig: ConfigENV['SERVER_CONFIG'];
  antflowExternalServices?: ConfigENV['EXTERNAL_SERVICES'];
  themes?: ConfigENV['THEMES'];
  projectFilterTags?: string[];
}

export const App: React.FC<Props> = ({ antflowServerConfig, antflowExternalServices, themes }) => {
  const authService = useMemo(
    () => new OIDCServiceFactory(antflowServerConfig.oidc).createService(),
    [antflowServerConfig.oidc]
  );
  const processingEndpoints = useMemo(
    () => new AntflowApiEndpoint(antflowServerConfig.apiUrl, authService),
    [antflowServerConfig.apiUrl, authService]
  );

  const offline = window.localStorage.getItem('offline');
  const [servicesUp, setServicesUp] = useState<AntFlowServiceStatusUp>({ trigger: false, spaces: false });
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const triggerHealthzUrl = antflowExternalServices?.TRIGGER?.healthzUrl;
    const spacesHealthzUrl = antflowExternalServices?.SPACES?.healthzUrl;
    const promises = [
      triggerHealthzUrl ? get(triggerHealthzUrl, { withCredentials: false }) : null,
      spacesHealthzUrl ? get(spacesHealthzUrl, { withCredentials: false }) : null
    ];

    // offline mode: skip auth initialization and directly check external services health
    // docs routes are public: skip auth bootstrap so `login-required` does not
    // force a redirect (which would also break on the HashRouter fragment).
    const isDocsRoute = window.location.hash.startsWith(`#${AppRoutes.docs.path}`);
    const authBootstrap =
      offline === 'true' || isDocsRoute
        ? Promise.resolve()
        : authService.initialize().catch((error: unknown) => {
            console.error('[Auth] Bootstrap initialization failed:', error);
          });

    Promise.all([authBootstrap, Promise.allSettled(promises)])
      .then(([, results]) => {
        const [triggerResult, spacesResult] = results;
        setServicesUp({
          trigger: triggerResult?.status === 'fulfilled' && triggerResult?.value?.status === 200,
          spaces: spacesResult?.status === 'fulfilled' && spacesResult?.value?.status === 200
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [antflowExternalServices, authService, offline]);

  const { ANTFLOW_APP_VERSION } = import.meta.env;

  if (loading) {
    return (
      <div className="flex flex-column w-full h-screen surface-card justify-content-center align-items-center">
        <Loader />
      </div>
    );
  }
  return (
    <HashRouter>
      <PrimeReactProvider>
        <QueryClientProvider client={new QueryClient({})}>
          <ThemeContextProvider brandThemes={themes}>
            <StoreContextProvider
              antflowProcessApiEndpoint={processingEndpoints}
              externalServiceConfig={{
                spaces: {
                  url: antflowExternalServices?.SPACES?.deployUrl,
                  serviceUp: servicesUp.spaces,
                  topic: antflowExternalServices?.SPACES?.topic
                },
                trigger: {
                  url: antflowExternalServices?.TRIGGER?.apiUrl,
                  serviceUp: servicesUp.trigger
                }
              }}
              antflowServerConfig={antflowServerConfig}
            >
              <Suspense
                fallback={
                  <div>
                    <Loader />
                  </div>
                }
              >
                <ErrorBoundaryWrapper>
                  <Routes>
                    {/* Public Routes */}
                    <Route
                      path={`${AppRoutes.docs.path}/*`}
                      element={
                        <main className="flex flex-column w-full h-full surface-card">
                          <HeaderNavbar servicesUp={servicesUp} showLoginButton />
                          <DocsPage appVersion={ANTFLOW_APP_VERSION} />
                        </main>
                      }
                    />

                    {/* Authenticated Routes */}
                    <Route
                      path="*"
                      element={
                        <GuardApp>
                          <ProcessesProvider
                            typeProcessing={TypeProcessing.ZeroProcess}
                            processesEndpoint={processingEndpoints}
                          >
                            <main className="flex flex-column min-h-screen surface-card">
                              <HeaderNavbar servicesUp={servicesUp} />
                              <AlertMessage />

                              <section className="flex-1 overflow-auto">
                                <Routes>
                                  <Route path={AppRoutes.projects.index.path} element={<ProjectsPage />} />
                                  <Route path={AppRoutes.jobs.index.path} element={<JobsPage />} />
                                  {servicesUp.trigger && (
                                    <Route path={AppRoutes.trigger.index.path} element={<TriggersPage />} />
                                  )}
                                  {servicesUp.spaces && (
                                    <Route path={AppRoutes.spaces.index.path} element={<SpacesPage />} />
                                  )}

                                  <Route path={'*'} element={<ProjectsPage />} />
                                </Routes>
                              </section>
                              <CopyRightFooter />
                            </main>
                          </ProcessesProvider>
                        </GuardApp>
                      }
                    />
                  </Routes>
                </ErrorBoundaryWrapper>
              </Suspense>
            </StoreContextProvider>
          </ThemeContextProvider>
        </QueryClientProvider>
      </PrimeReactProvider>
    </HashRouter>
  );
};
